import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppDispatch } from "@/store";
import { createManualPayment } from "@/store/modules/SubscriptionsAdmin/thunks";
import { manualPaymentSchema } from "@/validations";

// Tipos para contexto
interface ManualPaymentContext {
  subscription_id: number | string;
  client_subscription_id: number | string;
  assinatura_value?: number | string;
}

export const useSubscriptionManualPayment = (onSuccess?: () => void) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<ManualPaymentContext | null>(null);

  const todayStr = new Date().toISOString().slice(0, 10);
  const form = useForm({
    resolver: yupResolver(manualPaymentSchema),
    defaultValues: {
      value: 0,
      payment_method: "",
      description: "",
      payment_date: todayStr,
    },
  });

  // Permite setar dados de contexto (assinatura, cliente, valor padrão)
  const setContextData = (ctx: ManualPaymentContext) => {
    setContext(ctx);
    // Atualiza valor default do form
    if (ctx.assinatura_value) {
      form.setValue("value", Number(ctx.assinatura_value));
    }
  };

  const onSubmit = async (formData: any) => {
    if (!context) {
      setError("Contexto de pagamento manual não definido");
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      // Monta payload completo
      const today = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const payment_date = formData.payment_date || todayStr;
      const month = payment_date;
      const paid_value = formData.value;
      const payload = {
        subscription_id: context.subscription_id,
        client_subscription_id: context.client_subscription_id,
        month,
        paid_value,
        payment_method_id: formData.payment_method,
        payment_date,
        observation: formData.description || "",
        payment_observation: formData.description || "",
      };
      const result = await dispatch(
        createManualPayment({
          subscription_id: String(context.subscription_id),
          data: payload,
        }),
      );
      if (createManualPayment.fulfilled.match(result)) {
        if (onSuccess) onSuccess();
        setLoading(false);
        form.reset();
        return true;
      } else {
        // Tenta pegar a mensagem da API (diversos formatos possíveis)
        let apiMessage = "";
        if (result.payload) {
          if (typeof result.payload === "string") {
            try {
              const parsed = JSON.parse(result.payload);
              apiMessage = parsed.message || parsed.error || result.payload;
            } catch {
              apiMessage = result.payload;
            }
          } else if (
            typeof result.payload === "object" &&
            result.payload !== null
          ) {
            const payloadObj = result.payload as Record<string, any>;
            apiMessage = payloadObj.message || payloadObj.error || "";
          }
        }
        setError(apiMessage || "Erro ao criar pagamento manual");
        setLoading(false);
        return false;
      }
    } catch (err: any) {
      // Tenta extrair mensagem do erro da API
      let apiMessage = "";
      if (err?.response?.data) {
        if (typeof err.response.data === "string") {
          try {
            const parsed = JSON.parse(err.response.data);
            apiMessage = parsed.message || parsed.error || err.response.data;
          } catch {
            apiMessage = err.response.data;
          }
        } else {
          apiMessage =
            err.response.data.message || err.response.data.error || "";
        }
      }
      if (!apiMessage && err?.message) apiMessage = err.message;
      setError(apiMessage || "Erro desconhecido ao criar pagamento manual");
      setLoading(false);
      return false;
    }
  };

  return {
    ...form,
    onSubmit,
    setContextData,
    loading,
    error,
  };
};

export default useSubscriptionManualPayment;
